import { Mail, Phone, MapPin, ArrowUpRight, Clock, User } from "lucide-react";
import { COMPANY } from "../data/site";

export default function Contact() {
  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-bg border-t border-white/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14 md:mb-16">
          <div className="md:col-span-8 reveal">
            <div className="text-xs uppercase tracking-widest2 text-brand flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-brand" />
              Kontakt
            </div>
            <h2 className="font-display font-black text-white tracking-tightest leading-[0.95] text-4xl md:text-5xl lg:text-6xl">
              Spreman za sljedeći
              <br />
              <span className="text-brand">projekt?</span>
            </h2>
          </div>
          <p className="md:col-span-4 text-ink-muted leading-relaxed md:pb-3 reveal">
            Javite nam se za besplatnu konzultaciju i ponudu. Pokrivamo
            Varaždin i okolicu, a po dogovoru i šire područje Hrvatske.
          </p>
        </div>

        {/* Address + Hours strip */}
        <div className="grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-px">
          <div
            data-testid="contact-address"
            className="relative bg-surface p-8 md:p-10 reveal flex items-start gap-5"
          >
            <MapPin className="text-brand flex-shrink-0 mt-1" size={26} strokeWidth={1.5} />
            <div>
              <div className="text-[10px] uppercase tracking-widest2 text-ink-muted">
                Lokacija
              </div>
              <div className="mt-2 font-display font-bold text-white text-xl md:text-2xl tracking-tight">
                {COMPANY.address}
              </div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Ku%C4%8Danska+14+Vara%C5%BEdin"
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-brand hover:text-brand-light transition-colors"
              >
                Otvori u Google kartama <ArrowUpRight size={12} />
              </a>
            </div>
          </div>
          <div
            data-testid="contact-hours"
            className="relative bg-surface p-8 md:p-10 reveal flex items-start gap-5"
            style={{ animationDelay: "0.08s" }}
          >
            <Clock className="text-brand flex-shrink-0 mt-1" size={26} strokeWidth={1.5} />
            <div>
              <div className="text-[10px] uppercase tracking-widest2 text-ink-muted">
                Radno vrijeme
              </div>
              <div className="mt-2 font-display font-bold text-white text-xl md:text-2xl tracking-tight">
                {COMPANY.hours}
              </div>
              <div className="mt-2 text-xs text-ink-muted">
                Subota i nedjelja po dogovoru
              </div>
            </div>
          </div>
        </div>

        {/* Contact persons */}
        <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10 border-t-0">
          {COMPANY.contacts.map((c, i) => (
            <div
              key={c.email}
              data-testid={`contact-person-${i}`}
              className="relative bg-surface hover:bg-bg p-8 md:p-10 transition-colors group reveal"
              style={{ animationDelay: `${0.16 + i * 0.08}s` }}
            >
              <div className="flex items-start justify-between gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 grid place-items-center border border-white/10 bg-black/40 text-brand">
                    <User size={18} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest2 text-brand">
                      {c.role}
                    </div>
                    <div className="font-display font-bold text-white text-xl md:text-2xl tracking-tight mt-1">
                      {c.name}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <a
                  href={`tel:${c.phoneIntl}`}
                  data-testid={`contact-phone-${i}`}
                  className="flex items-center justify-between gap-4 py-4 border-t border-white/10 text-white hover:text-brand transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Phone size={16} className="text-ink-muted" />
                    <span className="font-display font-semibold text-lg tracking-tight">
                      {c.phone}
                    </span>
                  </div>
                  <ArrowUpRight size={16} className="slide-arrow text-ink-muted group-hover:text-brand" />
                </a>
                <a
                  href={`mailto:${c.email}`}
                  data-testid={`contact-email-${i}`}
                  className="flex items-center justify-between gap-4 py-4 border-t border-white/10 text-white hover:text-brand transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail size={16} className="text-ink-muted flex-shrink-0" />
                    <span className="font-display font-semibold text-sm md:text-base tracking-tight truncate">
                      {c.email}
                    </span>
                  </div>
                  <ArrowUpRight size={16} className="slide-arrow text-ink-muted flex-shrink-0 group-hover:text-brand" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Map */}
        <div
          data-testid="contact-map"
          className="mt-10 relative border border-white/10 overflow-hidden reveal"
        >
          <iframe
            title="Lokacija — Kučanska 14, Varaždin"
            src="https://www.google.com/maps?q=Ku%C4%8Danska%2014%2C%2042000%20Vara%C5%BEdin&output=embed"
            width="100%"
            height="460"
            style={{ border: 0 }}
            className="map-dark block"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/5" />
          <a
            href="https://www.google.com/maps/search/?api=1&query=Ku%C4%8Danska+14+Vara%C5%BEdin"
            target="_blank"
            rel="noreferrer"
            data-testid="open-in-maps"
            className="absolute top-4 right-4 inline-flex items-center gap-2 bg-black/80 backdrop-blur border border-white/10 hover:border-brand text-white px-4 py-2.5 text-[10px] uppercase tracking-widest2 transition-colors"
          >
            Otvori u Google kartama <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
